import Vue from 'vue'
import VueRouter from 'vue-router'
import { getAuth } from 'firebase/auth'
import { whenAuthBootstrapReady } from '@/services/authBootstrap'
import { getFirestore, doc, getDoc, getDocs, query, where, collection, limit } from 'firebase/firestore'
import { getMetaForRoute } from '@/config/meta'
import { isBasicRole } from '@/permissions/perms'
import { isPrerenderBuild } from '@/utils/prerender'

// ------------ Public Views (eager — core first paint) ------------
import Landing from '../views/public/Landing.vue'
import Lost from '../views/public/Lost.vue'
import Page from '../views/public/Page.vue'
import Region from '../views/public/Region.vue'
import Tour from '../views/public/Tour.vue'

// ------------ Public Views (lazy) ------------
const Booking = () => import(/* webpackChunkName: "booking" */ '../views/public/Booking.vue')
const BookingV2 = () => import(/* webpackChunkName: "booking-v2" */ '../views/public/BookingV2.vue')
const Gear = () => import(/* webpackChunkName: "public-extra" */ '../views/public/Gear.vue')
const Rentals = () => import(/* webpackChunkName: "public-extra" */ '../views/public/Rentals.vue')
const Tales = () => import(/* webpackChunkName: "public-extra" */ '../views/public/Tales.vue')
const ChoosingRenedianTour = () => import(/* webpackChunkName: "public-extra" */ '../views/public/ChoosingRenedianTour.vue')
const MeetTheGuides = () => import(/* webpackChunkName: "public-extra" */ '../views/public/MeetTheCrew.vue')
const ReneCormier = () => import(/* webpackChunkName: "public-extra" */ '../views/public/ReneCormier.vue')
const UniversityOfGravelRoads = () => import(/* webpackChunkName: "public-extra" */ '../views/public/UniversityOfGravelRoads.vue')
const VoicesOfExperience = () => import(/* webpackChunkName: "public-extra" */ '../views/public/VoicesOfExperience.vue')
const VideoSeries = () => import(/* webpackChunkName: "public-extra" */ '../views/public/VideoSeries.vue')
const RiderReview = () => import(/* webpackChunkName: "public-extra" */ '../views/public/RiderReview.vue')
const LocalLore = () => import(/* webpackChunkName: "public-extra" */ '../views/public/LocalLore.vue')
const ResourceArticle = () => import(/* webpackChunkName: "public-extra" */ '../views/public/ResourceArticle.vue')
const RiderStoryArticle = () => import(/* webpackChunkName: "public-extra" */ '../views/public/RiderStoryArticle.vue')
const TouringByMotorcycle = () => import(/* webpackChunkName: "public-extra" */ '../views/public/TouringByMotorcycle.vue')
const SideSteps = () => import(/* webpackChunkName: "public-extra" */ '../views/public/SideSteps.vue')
const Top10Lists = () => import(/* webpackChunkName: "public-extra" */ '../views/public/Top10Lists.vue')
const MotorcycleToursCalendar = () => import(/* webpackChunkName: "public-extra" */ '../views/public/Calendar.vue')

// ------------ Rider Portal Views (lazy) ------------
const PortalDashboard = () => import(/* webpackChunkName: "portal" */ '../views/portal/Dashboard.vue')
const PortalLogin = () => import(/* webpackChunkName: "portal" */ '../views/portal/Login.vue')
const PortalProfile = () => import(/* webpackChunkName: "portal" */ '../views/portal/Profile.vue')
const PortalTour = () => import(/* webpackChunkName: "portal" */ '../views/portal/Tour.vue')
const PortalBooking = () => import(/* webpackChunkName: "portal" */ '../views/portal/Booking.vue')
const PortalInvited = () => import(/* webpackChunkName: "portal" */ '../views/portal/Invited.vue')
const PortalAlumni = () => import(/* webpackChunkName: "portal" */ '../views/portal/Alumni.vue')
const PortalShop = () => import(/* webpackChunkName: "portal" */ '../views/portal/Shop.vue')
const PortalMessages = () => import(/* webpackChunkName: "portal" */ '../views/portal/Messages.vue')
const PortalInbox = () => import(/* webpackChunkName: "portal" */ '../views/portal/Inbox.vue')
const PortalInboxNew = () => import(/* webpackChunkName: "portal" */ '../views/portal/InboxNew.vue')
const PortalInboxThread = () => import(/* webpackChunkName: "portal" */ '../views/portal/InboxThread.vue')
const PortalLoop = () => import(/* webpackChunkName: "loop-page" */ '../views/portal/Loop.vue')
const MobileGuest = () => import(/* webpackChunkName: "mobile" */ '../views/mobile/Guest.vue')
const MobileCrew = () => import(/* webpackChunkName: "mobile" */ '../views/mobile/Crew.vue')

// ------------ Partner Views (lazy) ------------
const PartnerDashboard = () => import(/* webpackChunkName: "partner" */ '../views/partner/Dashboard.vue')
const PartnerLogin = () => import(/* webpackChunkName: "partner" */ '../views/partner/Login.vue')
const PartnerVehicles = () => import(/* webpackChunkName: "partner" */ '../views/partner/Vehicles.vue')

// ------------ Admin Views (lazy) ------------
const AdminDashboard = () => import(/* webpackChunkName: "admin" */ '../views/admin/dashboard/Dashboard.vue')
const AdminPending = () => import(/* webpackChunkName: "admin" */ '../views/admin/authentication/Pending.vue')
const AdminLogin = () => import(/* webpackChunkName: "admin" */ '../views/admin/authentication/Login.vue')
const AdminContentArticle = () => import(/* webpackChunkName: "admin" */ '../views/admin/storytelling/editorial/Article.vue')
const AdminStorytellingRiderVoice = () => import(/* webpackChunkName: "admin" */ '../views/admin/storytelling/RiderVoice.vue')
const AdminStorytellingEditorial = () => import(/* webpackChunkName: "admin" */ '../views/admin/storytelling/Editorial.vue')
const AdminManualBooking = () => import(/* webpackChunkName: "admin" */ '../views/admin/coordination/bookings/Manual.vue')
const AdminProductEconomics = () => import(/* webpackChunkName: "admin" */ '../views/admin/product/Economics.vue')
const AdminProductCostingTour = () => import(/* webpackChunkName: "admin" */ '../views/admin/product/economics/CostingTour.vue')
const AdminProductDevelopment = () => import(/* webpackChunkName: "admin" */ '../views/admin/product/Development.vue')
const AdminLeadershipPlanning = () => import(/* webpackChunkName: "admin" */ '../views/admin/leadership/Planning.vue')
const AdminLeadershipPeople = () => import(/* webpackChunkName: "admin" */ '../views/admin/leadership/People.vue')
const AdminLeadershipInsights = () => import(/* webpackChunkName: "admin" */ '../views/admin/leadership/Insights.vue')
const AdminLeadershipPolicies = () => import(/* webpackChunkName: "admin" */ '../views/admin/leadership/Policies.vue')
const AdminContactsPortalRedirect = () => import(/* webpackChunkName: "admin" */ '../views/admin/leadership/people/PortalRedirect.vue')
const AdminTechnicalConfiguration = () => import(/* webpackChunkName: "admin" */ '../views/admin/technical/Configuration.vue')
const AdminTechnicalPlatform = () => import(/* webpackChunkName: "admin" */ '../views/admin/technical/Platform.vue')
const AdminTechnicalEmail = () => import(/* webpackChunkName: "admin" */ '../views/admin/technical/Email.vue')
const AdminTechnicalIntegrations = () => import(/* webpackChunkName: "admin" */ '../views/admin/technical/Integrations.vue')
const AdminTechnicalModels = () => import(/* webpackChunkName: "admin" */ '../views/admin/technical/Models.vue')
const AdminTechnicalAgents = () => import(/* webpackChunkName: "admin" */ '../views/admin/technical/Agents.vue')
const AdminTechnicalLab = () => import(/* webpackChunkName: "admin" */ '../views/admin/technical/Lab.vue')
const AdminDeveloperAPIKeys = () => import(/* webpackChunkName: "admin" */ '../views/admin/technical/integrations/Stripe.vue')
const AdminAutomationAI = () => import(/* webpackChunkName: "admin" */ '../views/admin/technical/integrations/Google.vue')
const AdminGuideTours = () => import(/* webpackChunkName: "admin" */ '../views/admin/guide/Tours.vue')
const CoordinationToursList = () => import(/* webpackChunkName: "admin" */ '../views/admin/coordination/Tours.vue')
const CoordinationTour = () => import(/* webpackChunkName: "admin" */ '../views/admin/coordination/tours/Tour.vue')
const CoordinationBookingsList = () => import(/* webpackChunkName: "admin" */ '../views/admin/coordination/Bookings.vue')
const CoordinationBooking = () => import(/* webpackChunkName: "admin" */ '../views/admin/coordination/bookings/Booking.vue')
const CoordinationVendors = () => import(/* webpackChunkName: "admin" */ '../views/admin/coordination/Vendors.vue')
const ProductTemplate = () => import(/* webpackChunkName: "admin" */ '../views/admin/product/development/Template.vue')
const ProductExperience = () => import(/* webpackChunkName: "admin" */ '../views/admin/product/development/Experience.vue')
const ProductTour = () => import(/* webpackChunkName: "admin" */ '../views/admin/product/development/Tour.vue')
const AdminGuideProfile = () => import(/* webpackChunkName: "admin" */ '../views/admin/guide/Profile.vue')
const AdminCalendar = () => import(/* webpackChunkName: "admin" */ '../views/admin/calendar/Calendar.vue')
const AdminFinanceReceivables = () => import(/* webpackChunkName: "admin" */ '../views/admin/finance/Receivables.vue')
const AdminFinancePayables = () => import(/* webpackChunkName: "admin" */ '../views/admin/finance/Payables.vue')
const AdminFinanceTours = () => import(/* webpackChunkName: "admin" */ '../views/admin/finance/Tours.vue')
const AdminFinanceTour = () => import(/* webpackChunkName: "admin" */ '../views/admin/finance/Tour.vue')
const AdminFinanceBooks = () => import(/* webpackChunkName: "admin" */ '../views/admin/finance/Books.vue')
const AdminGrowthGenerate = () => import(/* webpackChunkName: "admin" */ '../views/admin/growth/Generate.vue')
const AdminGrowthSales = () => import(/* webpackChunkName: "admin" */ '../views/admin/growth/Sales.vue')
const AdminSupportTourSupport = () => import(/* webpackChunkName: "admin" */ '../views/admin/support/TourSupport.vue')
const AdminSupportTickets = () => import(/* webpackChunkName: "admin" */ '../views/admin/support/Tickets.vue')
const AdminOperationsActivities = () => import(/* webpackChunkName: "admin" */ '../views/admin/coordination/vendors/Activities.vue')
const AdminVendorsActivity = () => import(/* webpackChunkName: "admin" */ '../views/admin/coordination/vendors/Activity.vue')
const AdminVendorsHotel = () => import(/* webpackChunkName: "admin" */ '../views/admin/coordination/vendors/Accommodation.vue')
const AdminOperationsAccommodations = () => import(/* webpackChunkName: "admin" */ '../views/admin/coordination/vendors/Accommodations.vue')
const AdminVendorsRestaurant = () => import(/* webpackChunkName: "admin" */ '../views/admin/coordination/vendors/Restaurant.vue')
const AdminOperationsRestaurants = () => import(/* webpackChunkName: "admin" */ '../views/admin/coordination/vendors/Restaurants.vue')
const AdminOperationsVehicles = () => import(/* webpackChunkName: "admin" */ '../views/admin/coordination/vendors/Vehicles.vue')

Vue.use(VueRouter)

const routes = [
  {
    path: '/resources/:tab/:slug',
    name: 'ResourceArticle',
    component: ResourceArticle,
    meta: {
      scope: 'public',
      title: 'Resource Article'
    }
  },
  // ---------------------- Public Routes ----------------------
  {
    path: '/booking',
    name: 'Booking',
    component: Booking,
    meta: {
      scope: 'public',
      title: 'Tour Booking'
    }
  },
  {
    path: '/booking/v2',
    name: 'BookingV2',
    component: BookingV2,
    meta: {
      scope: 'public',
      title: 'Tour Booking'
    }
  },
  {
    path: '/booking/v2/:id',
    name: 'BookingV2WithId',
    component: BookingV2,
    meta: {
      scope: 'public',
      title: 'Tour Booking'
    }
  },
  {
    path: '/booking/:id',
    name: 'BookingWithId',
    component: Booking,
    meta: {
      scope: 'public',
      title: 'Tour Booking'
    }
  },
  {
    path: '/choosing-renedian',
    name: 'ChoosingRenedian',
    component: ChoosingRenedianTour,
    meta: {
      scope: 'public',
      title: 'Why choose Renedian?',
      description: 'Discover why riders choose Renedian for their motorcycle adventures. Expert guides, premium support vehicles, small groups, and 15+ years of experience.'
    }
  },
  {
    path: '/choosing-renedian/meet-the-guides/:profile',
    redirect: (to) => `/choosing-renedian/meet-the-crew/${to.params.profile}`
  },
  {
    path: '/choosing-renedian/meet-the-guides',
    redirect: '/choosing-renedian/meet-the-crew'
  },
  {
    path: '/choosing-renedian/meet-the-crew',
    name: 'MeetTheGuides',
    component: MeetTheGuides,
    meta: {
      scope: 'public',
      title: 'Meet the Crew',
      description: 'Meet the crew who will be supporting and leading your motorcycle adventure.'
    }
  },
  {
    path: '/choosing-renedian/about-the-founder',
    name: 'ReneCormier',
    component: ReneCormier,
    meta: {
      scope: 'public',
      title: 'About the Founder',
      description: 'Learn about the founder of Renedian.'
    }
  },
  {
    path: '/choosing-renedian/university-of-gravel-roads',
    name: 'UniversityOfGravelRoads',
    component: UniversityOfGravelRoads,
    meta: {
      scope: 'public',
      title: 'University of Gravel Roads',
      description: 'Learn about the University of Gravel Roads.'
    }
  },
  {
    path: '/rider-stories/voices-of-experience',
    name: 'VoicesOfExperience',
    component: VoicesOfExperience,
    meta: {
      scope: 'public',
      title: 'Voices of Experience',
      description: 'Learn about the Voices of Experience.'
    }
  },
  {
    path: '/rider-stories/video-series',
    name: 'VideoSeries',
    component: VideoSeries,
    meta: {
      scope: 'public',
      title: 'Video Series',
      description: 'Learn about the Video Series.'
    }
  },
  {
    path: '/rider-stories/rider-reviews',
    name: 'RiderReview',
    component: RiderReview,
    meta: {
      scope: 'public',
      title: 'Rider Reviews',
      description: 'Learn about the Rider Reviews.'
    }
  },
  {
    path: '/rider-stories/:section/:slug',
    name: 'RiderStoryArticle',
    component: RiderStoryArticle,
    meta: {
      scope: 'public',
      title: 'Rider Story Article'
    }
  },
  {
    path: '/resources/local-lore',
    name: 'LocalLore',
    component: LocalLore,
    meta: {
      scope: 'public',
      title: 'Local Lore',
      description: 'Learn about the Local Lore.'
    }
  },
  {
    path: '/resources/touring-by-motorcycle',
    name: 'TouringByMotorcycle',
    component: TouringByMotorcycle,
    meta: {
      scope: 'public',
      title: 'Touring by Motorcycle',
      description: 'Learn about the Touring by Motorcycle.'
    }
  },
  {
    path: '/resources/side-steps',
    name: 'SideSteps',
    component: SideSteps,
    meta: {
      scope: 'public',
      title: 'Side Steps',
      description: 'Learn about the Side Steps.'
    }
  },
  {
    path: '/resources/top-10-lists',
    name: 'Top10Lists',
    component: Top10Lists,
    meta: {
      scope: 'public',
      title: 'Top 10 Lists',
      description: 'Learn about the Top 10 Lists.'
    }
  },
  {
    path: '/gear',
    name: 'Gear',
    component: Gear,
    meta: {
      scope: 'public',
      title: 'Gear'
    }
  },
  {
    path: '/',
    name: 'Landing',
    component: Landing,
    meta: {
      scope: 'public',
      // template: 'Experimental',
      // template: 'Mockup',
    }
  },

  {
    path: '/motorcycle-rentals',
    name: 'Rentals',
    component: Rentals,
    meta: {
      scope: 'public',
      title: 'Motorcycle Rentals'
    }
  },
  {
    path: '/rider-tales',
    name: 'Tales',
    component: Tales,
    meta: {
      scope: 'public',
      title: 'Rider Tales'
    }
  },
  // ---------------------- Public Routes - Tours ----------------------
  {
    path: '/calendar',
    name: 'Calendar',
    component: MotorcycleToursCalendar,
    meta: {
      scope: 'public',
      title: 'Calendar',
      description: 'View all upcoming Renedian adventure motorcycle tours and departures.'
    }
  },
  {
    path: '/adventure-motorcycle-tours/:region',
    name: 'Region',
    component: Region,
    meta: {
      scope: 'public'
    }
  },
  // ---------------------- Dynamic Tour Route (MUST be last) ----------------------
  {
    path: '/adventure-motorcycle-tours/:continent/:slug',
    name: 'TourDynamic',
    component: Tour,
    meta: {
      scope: 'public'
    }
  },
  // ---------------------- Portal Routes ----------------------
  {
    path: '/portal',
    name: 'PortalDashboard',
    component: PortalDashboard,
    meta: {
      scope: 'private',
      template: 'Portal',
      title: 'Dashboard - Rider Portal'
    }
  },
  {
    path: '/portal/login',
    name: 'PortalLogin',
    component: PortalLogin,
    meta: {
      scope: 'public',
      authenticatedRoute: 'PortalDashboard',
      title: 'Portal - Login'
    }
  },
  {
    path: '/portal/account',
    name: 'PortalAccount',
    component: PortalProfile,
    meta: {
      scope: 'private',
      template: 'Portal',
      title: 'My Account - Rider Portal'
    }
  },
  {
    // Legacy profile route - redirect to account, preserving any params (e.g. tab)
    path: '/portal/profile',
    name: 'PortalProfile',
    redirect: to => ({ name: 'PortalAccount', params: to.params }),
  },
  {
    path: '/portal/tour/:index',
    name: 'PortalTour',
    component: PortalTour,
    meta: {
      scope: 'private',
      template: 'Portal',
      title: 'Tour - Rider Portal'
    }
  },
  {
    path: '/portal/invited',
    name: 'PortalInvited',
    component: PortalInvited,
    meta: {
      scope: 'private',
      template: 'Portal',
      title: 'Invited Tours - Rider Portal'
    }
  },
  {
    path: '/portal/alumni',
    name: 'PortalAlumni',
    component: PortalAlumni,
    meta: {
      scope: 'private',
      template: 'Portal',
      title: 'Alumni - Rider Portal'
    }
  },
  {
    path: '/portal/shop',
    name: 'PortalShop',
    component: PortalShop,
    meta: {
      scope: 'private',
      template: 'Portal',
      title: 'Shop - Rider Portal'
    }
  },
  {
    // The envelope: "How can we help?" and the rider's support tickets.
    path: '/portal/inbox',
    name: 'PortalInbox',
    component: PortalInbox,
    meta: { scope: 'private', template: 'Portal', title: 'Inbox - Rider Portal' }
  },
  {
    path: '/portal/inbox/new/:door?',
    name: 'PortalInboxNew',
    component: PortalInboxNew,
    meta: { scope: 'private', template: 'Portal', title: 'Write to us - Rider Portal' }
  },
  {
    path: '/portal/inbox/:ticketId',
    name: 'PortalInboxThread',
    component: PortalInboxThread,
    meta: { scope: 'private', template: 'Portal', title: 'Inbox - Rider Portal' }
  },
  {
    // Deep link to the tour's group chat (push notifications); not a page of its own.
    path: '/portal/messages',
    name: 'PortalMessages',
    component: PortalMessages,
    meta: {
      scope: 'private',
      template: 'Portal',
      title: 'Group Chat - Rider Portal'
    }
  },
  {
    path: '/portal/booking',
    name: 'PortalBooking',
    component: PortalBooking,
    meta: {
      scope: 'private',
      template: 'Portal',
      title: 'Book a Tour - Rider Portal'
    }
  },
  {
    path: '/portal/booking/:id',
    name: 'PortalBookingWithId',
    component: PortalBooking,
    meta: {
      scope: 'private',
      template: 'Portal',
      title: 'Book a Tour - Rider Portal'
    }
  },
  {
    path: '/portal/loop',
    name: 'PortalLoop',
    component: PortalLoop,
    meta: {
      scope: 'private',
      template: 'Portal',
      title: 'Route - Rider Portal'
    }
  },
  // ---------------------- Mobile app prototypes ----------------------
  // Phone-shaped walkthroughs of the two Flutter apps (apps/mobile/guest and /crew),
  // on the portal's real data, so the experience is settled before it is built native.
  {
    path: '/mobile/guest',
    name: 'MobileGuest',
    component: MobileGuest,
    meta: { scope: 'private', template: 'Mobile', title: 'Renedian' }
  },
  {
    path: '/mobile/crew',
    name: 'MobileCrew',
    component: MobileCrew,
    meta: { scope: 'admin', template: 'Mobile', title: 'Renedian Crew' }
  },
  { path: '/portal/book', redirect: { name: 'PortalBooking' } },
  { path: '/portal/book/:id', redirect: to => ({ name: 'PortalBookingWithId', params: { id: to.params.id } }) },
  // ---------------------- Partner Routes ----------------------
  {
    path: '/partner',
    name: 'PartnerDashboard',
    component: PartnerDashboard,
    meta: {
      scope: 'private',
      template: 'Partner',
      loginRoute: 'PartnerLogin',
      title: 'Dashboard - Partner Portal'
    }
  },
  {
    path: '/vendor',
    redirect: '/partner'
  },
  {
    path: '/partner/login',
    name: 'PartnerLogin',
    component: PartnerLogin,
    meta: {
      scope: 'public',
      authenticatedRoute: 'PartnerDashboard',
      title: 'Partner - Login'
    }
  },
  {
    path: '/vendor/login',
    redirect: '/partner/login'
  },
  {
    path: '/partner/vehicles',
    name: 'PartnerVehicles',
    component: PartnerVehicles,
    meta: {
      scope: 'private',
      template: 'Partner',
      loginRoute: 'PartnerLogin',
      title: 'Vehicles - Partner Portal'
    }
  },
  {
    path: '/vendor/vehicles',
    redirect: '/partner/vehicles'
  },
  // ---------------------- Admin Routes ----------------------
  {
    path: '/springbok/login',
    name: 'AdminLogin',
    component: AdminLogin,
    meta: {
      scope: 'public',
      authenticatedRoute: 'AdminDashboard',
      title: 'Admin Login'
    }
  },
  {
    path: '/springbok',
    name: 'AdminDashboard',
    component: AdminDashboard,
    meta: {
      scope: 'admin',
      template: 'Admin',
      title: 'Taskbox'
    }
  },
  {
    path: '/springbok/pending',
    name: 'AdminPending',
    component: AdminPending,
    meta: {
      scope: 'admin',
      title: 'Admin Pending'
    }
  },
  // ------------ Bookings ------------
  {
    path: '/springbok/coordination/bookings',
    name: 'CoordinationBookings',
    component: CoordinationBookingsList,
    meta: {
      scope: 'admin',
      template: 'Admin',
      title: 'Coordination - Bookings',
      role: 'coordination'
    }
  },
  {
    path: '/springbok/coordination/bookings/create',
    name: 'CoordinationBookingsCreate',
    component: AdminManualBooking,
    meta: {
      scope: 'admin',
      template: 'Admin',
      title: 'Coordination - Create Booking',
      role: 'coordination'
    }
  },
  {
    path: '/springbok/coordination/bookings/:id',
    name: 'CoordinationBooking',
    component: CoordinationBooking,
    meta: {
      scope: 'admin',
      template: 'Admin',
      title: 'Coordination - Booking',
      role: 'coordination'
    }
  },
  { path: '/springbok/operations/bookings/create', redirect: { name: 'CoordinationBookingsCreate' } },
  { path: '/springbok/operations/bookings', redirect: { name: 'CoordinationBookings' } },

  // Storytelling — Rider Voice (Rider Stories + Reviews)
  {
    path: '/springbok/storytelling/rider-voice',
    name: 'AdminStorytellingRiderVoice',
    component: AdminStorytellingRiderVoice,
    meta: { scope: 'admin', template: 'Admin', title: 'Storytelling - Rider Voice', role: 'storytelling' }
  },
  // Storytelling — Editorial (Articles + Pages)
  {
    path: '/springbok/storytelling/editorial',
    name: 'AdminStorytellingEditorial',
    component: AdminStorytellingEditorial,
    meta: { scope: 'admin', template: 'Admin', title: 'Storytelling - Editorial', role: 'storytelling' }
  },
  // Article detail route (accessed from within the Editorial > Articles tab)
  {
    path: '/springbok/growth/articles/:id',
    name: 'AdminGrowthArticle',
    component: AdminContentArticle,
    meta: { scope: 'admin', template: 'Admin', title: 'Growth - Article', role: 'storytelling' }
  },
  // Legacy redirects
  { path: '/springbok/growth/reviews', redirect: { name: 'AdminStorytellingRiderVoice', query: { tab: 'reviews' } } },
  { path: '/springbok/growth/articles', redirect: { name: 'AdminStorytellingEditorial', query: { tab: 'articles' } } },
  { path: '/springbok/growth/pages', redirect: { name: 'AdminStorytellingEditorial', query: { tab: 'pages' } } },
  // Growth
  { path: '/springbok/growth', redirect: { name: 'AdminGrowthGenerate' } },
  {
    path: '/springbok/growth/generate',
    name: 'AdminGrowthGenerate',
    component: AdminGrowthGenerate,
    meta: { scope: 'admin', template: 'Admin', title: 'Growth - Generate', role: 'growth' },
  },
  {
    path: '/springbok/growth/sales',
    name: 'AdminGrowthSales',
    component: AdminGrowthSales,
    meta: { scope: 'admin', template: 'Admin', title: 'Growth - Sales', role: 'growth' },
  },
  {
    path: '/springbok/growth/alumni',
    name: 'AdminGrowthAlumni',
    redirect: { name: 'AdminGrowthGenerate', query: { tab: 'alumni' } },
  },
  // Legacy redirects
  { path: '/springbok/growth/campaigns', name: 'AdminGrowthCampaignsTabs', redirect: { name: 'AdminGrowthGenerate', query: { tab: 'campaigns' } } },
  { path: '/springbok/growth/leads', name: 'AdminGrowthLeads', redirect: { name: 'AdminGrowthSales', query: { tab: 'leads' } } },
  { path: '/springbok/growth/social-media', redirect: { name: 'AdminGrowthGenerate', query: { tab: 'campaigns' } } },
  { path: '/springbok/growth/promotions', redirect: { name: 'AdminGrowthGenerate', query: { tab: 'alumni' } } },
  { path: '/springbok/growth/interest', redirect: { name: 'AdminGrowthSales', query: { tab: 'leads' } } },

  // ------------ Operations ------------
  {
    path: '/springbok/operations/activities',
    name: 'AdminOperationsActivities',
    component: AdminOperationsActivities,
    meta: {
      scope: 'admin',
      template: 'Admin',
      title: 'Operations - Activities',
      role: 'coordination'
    }
  },
  {
    path: '/springbok/operations/activities/:id',
    name: 'AdminOperationsActivity',
    component: AdminVendorsActivity,
    meta: {
      scope: 'admin',
      template: 'Admin',
      title: 'Operations - Activity',
      role: 'coordination'
    }
  },
  {
    path: '/springbok/operations/accommodations',
    name: 'AdminOperationsHotels',
    component: AdminOperationsAccommodations,
    meta: {
      scope: 'admin',
      template: 'Admin',
      title: 'Operations - Accommodations',
      role: 'coordination'
    }
  },
  {
    path: '/springbok/operations/accommodations/new',
    name: 'AdminOperationsHotelCreate',
    component: AdminVendorsHotel,
    meta: {
      scope: 'admin',
      template: 'Admin',
      title: 'Operations - Add Accommodation',
      role: 'coordination'
    }
  },
  {
    path: '/springbok/operations/accommodations/:id',
    name: 'AdminOperationsHotelEdit',
    component: AdminVendorsHotel,
    meta: {
      scope: 'admin',
      template: 'Admin',
      title: 'Operations - Accommodation',
      role: 'coordination'
    }
  },
  {
    path: '/springbok/operations/restaurants/:id',
    name: 'AdminOperationsRestaurant',
    component: AdminVendorsRestaurant,
    meta: {
      scope: 'admin',
      template: 'Admin',
      title: 'Operations - Restaurant',
      role: 'coordination'
    }
  },
  {
    path: '/springbok/operations/restaurants',
    name: 'AdminOperationsRestaurants',
    component: AdminOperationsRestaurants,
    meta: {
      scope: 'admin',
      template: 'Admin',
      title: 'Operations - Restaurants',
      role: 'coordination'
    }
  },
  {
    path: '/springbok/coordination/vendors',
    name: 'CoordinationVendors',
    component: CoordinationVendors,
    meta: {
      scope: 'admin',
      template: 'Admin',
      title: 'Coordination - Vendors',
      role: 'coordination'
    }
  },
  { path: '/springbok/operations/vendors',        redirect: { name: 'CoordinationVendors' } },
  { path: '/springbok/operations/vehicles',       redirect: { name: 'CoordinationVendors' } },
  { path: '/springbok/operations/accommodations', redirect: { name: 'CoordinationVendors' } },
  { path: '/springbok/operations/restaurants',    redirect: { name: 'CoordinationVendors' } },
  { path: '/springbok/operations/activities',     redirect: { name: 'CoordinationVendors' } },

  // ------------ Customer Service (legacy redirects) ------------
  { path: '/springbok/customer-service/bookings', redirect: { name: 'CoordinationBookings' } },
  { path: '/springbok/customer-service/bookings/create', redirect: { name: 'CoordinationBookingsCreate' } },
  { path: '/springbok/customer-service/customers', redirect: { name: 'AdminLeadershipPeople', query: { tab: 'contacts' } } },

  // ------------ Leadership ------------
  {
    path: '/springbok/leadership/people',
    name: 'AdminLeadershipPeople',
    component: AdminLeadershipPeople,
    meta: {
      scope: 'admin',
      template: 'Admin',
      title: 'Leadership - People',
      role: 'leadership'
    }
  },
  // Legacy standalone routes — kept for backward compat, redirect to grouped pages
  {
    path: '/springbok/leadership/employees',
    name: 'AdminLeadershipEmployees',
    redirect: { name: 'AdminLeadershipPeople', query: { tab: 'employees' } },
  },
  {
    path: '/springbok/leadership/roles',
    name: 'AdminLeadershipRoles',
    redirect: { name: 'AdminLeadershipPeople', query: { tab: 'employees', section: 'roles' } },
  },
  {
    path: '/springbok/leadership/vendors',
    name: 'AdminLeadershipVendors',
    redirect: '/springbok/operations/vendors',
    meta: {
      scope: 'admin',
      template: 'Admin',
      title: 'Leadership - Vendors',
      role: 'coordination'
    }
  },

  // ------------ People (legacy/non-menu, keep available) ------------
  {
    path: '/springbok/leadership/contacts',
    name: 'AdminContacts',
    redirect: { name: 'AdminLeadershipPeople', query: { tab: 'contacts' } },
  },
  {
    path: '/springbok/people/contacts/portal/:email',
    name: 'AdminContactsPortalRedirect',
    component: AdminContactsPortalRedirect,
    meta: {
      scope: 'admin',
      template: 'Admin',
      title: 'Admin People - Contacts - Portal',
      role: 'support'
    }
  },
  {
    path: '/springbok/people/employees',
    name: 'AdminEmployees',
    redirect: { name: 'AdminLeadershipPeople', query: { tab: 'employees' } },
    meta: {
      scope: 'admin',
      template: 'Admin',
      title: 'Admin People - Employees',
      role: 'leadership'
    }
  },
  {
    path: '/springbok/people/vendors',
    name: 'AdminPeopleVendors',
    redirect: '/springbok/operations/vendors',
    meta: {
      scope: 'admin',
      template: 'Admin',
      title: 'Admin People - Vendors',
      role: 'coordination'
    }
  },
  // ------------ Support ------------
  {
    path: '/springbok/support/tour',
    name: 'AdminSupportTourSupport',
    component: AdminSupportTourSupport,
    meta: {
      scope: 'admin',
      template: 'Admin',
      title: 'Support - Tour Support',
      role: 'support',
    },
  },
  {
    path: '/springbok/support/tickets',
    name: 'AdminSupportTickets',
    component: AdminSupportTickets,
    meta: {
      scope: 'admin',
      template: 'Admin',
      title: 'Support - Tickets',
      role: 'support',
    },
  },
  // The board replaced the Triage list and the Intake list; Intake is its first lane.
  { path: '/springbok/support/guest', redirect: { name: 'AdminSupportTickets' } },
  { path: '/springbok/support/intake', redirect: { name: 'AdminSupportTickets' } },
  { path: '/springbok/support', redirect: { name: 'AdminSupportTickets' } },

  // ------------ Customer Service (legacy redirects) ------------
  {
    path: '/springbok/customer-service/inbox',
    name: 'AdminCustomerServiceInbox',
    redirect: { name: 'AdminSupportTickets' },
    meta: {
      scope: 'admin',
      template: 'Admin',
      title: 'Customer Service - Inbox',
      role: 'support'
    }
  },
  {
    path: '/springbok/customer-service/tickets',
    name: 'AdminCustomerServiceTickets',
    redirect: { name: 'AdminSupportTickets' },
    meta: {
      scope: 'admin',
      template: 'Admin',
      title: 'Customer Service - Tickets',
      role: 'support'
    }
  },
  // ------------ Tours ------------
  {
    path: '/springbok/coordination/tours',
    name: 'CoordinationTours',
    component: CoordinationToursList,
    meta: {
      scope: 'admin',
      template: 'Admin',
      title: 'Coordination - Tours',
      role: 'coordination'
    }
  },
  { path: '/springbok/operations/tours', redirect: { name: 'CoordinationTours' } },
  {
    path: '/springbok/product/regions',
    name: 'ProductRegions',
    redirect: { name: 'AdminProductDevelopment', query: { tab: 'regions' } },
  },
  { path: '/springbok/operations/regions', redirect: { name: 'ProductRegions' } },

  {
    path: '/springbok/coordination/tours/:id',
    name: 'CoordinationTour',
    component: CoordinationTour,
    meta: {
      scope: 'admin',
      template: 'Admin',
      title: 'Coordination - Tour',
      role: 'coordination'
    }
  },
  // Legacy redirect
  {
    path: '/springbok/operations/tours/:id',
    redirect: to => ({ name: 'CoordinationTour', params: { id: to.params.id } }),
  },
  {
    path: '/springbok/product/templates/:id',
    name: 'ProductTemplate',
    component: ProductTemplate,
    meta: {
      scope: 'admin',
      template: 'Admin',
      title: 'Product - Template',
      role: 'product'
    }
  },
  { path: '/springbok/operations/templates/:id', redirect: to => ({ name: 'ProductTemplate', params: { id: to.params.id } }) },
  {
    path: '/springbok/product/experiences/:id',
    name: 'ProductExperience',
    component: ProductExperience,
    meta: {
      scope: 'admin',
      template: 'Admin',
      title: 'Product - Tour Template',
      role: 'product',
    },
  },
  {
    path: '/springbok/product/experiences',
    name: 'ProductExperiences',
    redirect: { name: 'AdminProductDevelopment', query: { tab: 'experiences' } },
  },
  {
    path: '/springbok/product/templates',
    name: 'ProductTemplates',
    redirect: { name: 'AdminProductDevelopment', query: { tab: 'templates' } },
  },
  { path: '/springbok/operations/templates', redirect: { name: 'ProductTemplates' } },
  {
    path: '/springbok/product/development',
    name: 'AdminProductDevelopment',
    component: AdminProductDevelopment,
    meta: {
      scope: 'admin',
      template: 'Admin',
      title: 'Product - Development',
      role: 'product',
    },
  },

  // ------------ Technical ------------
  { path: '/springbok/technical', redirect: '/springbok/technical/configuration' },
  {
    path: '/springbok/technical/configuration',
    name: 'AdminTechnicalConfiguration',
    component: AdminTechnicalConfiguration,
    meta: { scope: 'admin', template: 'Admin', title: 'Technical - Configuration', role: 'technical' },
  },
  {
    path: '/springbok/technical/platform',
    name: 'AdminTechnicalPlatform',
    component: AdminTechnicalPlatform,
    meta: { scope: 'admin', template: 'Admin', title: 'Technical - Platform', role: 'technical' }
  },
  {
    path: '/springbok/technical/email',
    name: 'AdminTechnicalEmail',
    component: AdminTechnicalEmail,
    meta: { scope: 'admin', template: 'Admin', title: 'Technical - Email', role: 'technical' }
  },
  {
    path: '/springbok/technical/integrations',
    name: 'AdminTechnicalIntegrations',
    component: AdminTechnicalIntegrations,
    meta: { scope: 'admin', template: 'Admin', title: 'Technical - Integrations', role: 'technical' }
  },
  {
    path: '/springbok/technical/models',
    name: 'AdminTechnicalModels',
    component: AdminTechnicalModels,
    meta: { scope: 'admin', template: 'Admin', title: 'Technical - Models', role: 'technical' }
  },
  {
    path: '/springbok/technical/agents',
    name: 'AdminTechnicalAgents',
    component: AdminTechnicalAgents,
    meta: { scope: 'admin', template: 'Admin', title: 'Technical - Agents', role: 'technical' }
  },
  {
    path: '/springbok/technical/lab',
    name: 'AdminTechnicalLab',
    component: AdminTechnicalLab,
    meta: { scope: 'admin', template: 'Admin', title: 'Technical - Lab', role: 'technical' }
  },
  // Legacy redirects
  { path: '/springbok/system/email', redirect: { name: 'AdminTechnicalEmail', query: { tab: 'planning' } } },
  { path: '/springbok/system/email-templates', redirect: { name: 'AdminTechnicalEmail', query: { tab: 'templates' } } },
  { path: '/springbok/system/templates', redirect: { name: 'AdminTechnicalEmail', query: { tab: 'templates' } } },
  { path: '/springbok/system/meal-rates', redirect: { name: 'AdminTechnicalConfiguration', query: { tab: 'meal-rates' } } },
  { path: '/springbok/system/taxation', redirect: { name: 'AdminTechnicalConfiguration', query: { tab: 'taxation' } } },
  { path: '/springbok/system/alerts', redirect: { name: 'AdminTechnicalPlatform', query: { tab: 'alerts' } } },
  { path: '/springbok/system/cache', redirect: { name: 'AdminTechnicalPlatform', query: { tab: 'cache' } } },
  { path: '/springbok/system/redirects', redirect: { name: 'AdminTechnicalPlatform', query: { tab: 'redirects' } } },
  { path: '/springbok/developer/ai', redirect: { name: 'AdminTechnicalIntegrations', query: { tab: 'openrouter' } } },
  { path: '/springbok/developer/api-keys', redirect: { name: 'AdminTechnicalIntegrations', query: { tab: 'stripe' } } },

  {
    path: '/springbok/developer/ai-legacy',
    name: 'AdminDeveloperAI',
    component: AdminAutomationAI,
    meta: {
      scope: 'admin',
      template: 'Admin',
      title: 'Developer - AI',
      role: 'technical'
    }
  },
  // ------------ Insights (legacy redirects) ------------
  { path: '/springbok/insights/costing',  redirect: { name: 'AdminProductEconomics', query: { tab: 'costing' } } },
  { path: '/springbok/insights/metrics',  redirect: { name: 'AdminLeadershipInsights' } },
  { path: '/springbok/insights/overview', redirect: { name: 'AdminLeadershipInsights' } },


  // ------------ Guides ------------
  { path: '/springbok/guide', redirect: '/springbok/guide/tours' },
  {
    path: '/springbok/guide/tours',
    name: 'AdminGuideTours',
    component: AdminGuideTours,
    meta: { scope: 'admin', template: 'Admin', title: 'Guide - Tours', role: 'guide' }
  },
  {
    path: '/springbok/guide/profile',
    name: 'AdminGuideProfile',
    component: AdminGuideProfile,
    meta: { scope: 'admin', template: 'Admin', title: 'Guide - Profile', role: 'guide' }
  },
  // Legacy redirects
  { path: '/springbok/guide/current',  redirect: { name: 'AdminGuideTours', query: { tab: 'on-tour' } } },
  { path: '/springbok/guide/upcoming', redirect: { name: 'AdminGuideTours', query: { tab: 'upcoming' } } },
  { path: '/springbok/guide/schedule',  redirect: { name: 'AdminGuideTours', query: { tab: 'pre-tour' } } },
  { path: '/springbok/guide/documents', redirect: '/springbok/guide/profile' },
  {
    path: '/springbok/developer',
    redirect: '/springbok/developer/fix'
  },

  // ------------ Universal: Calendar (all employees) ------------
  { path: '/springbok/schedule', redirect: '/springbok/calendar' },
  {
    path: '/springbok/calendar',
    name: 'AdminCalendar',
    component: AdminCalendar,
    meta: {
      scope: 'admin',
      template: 'Admin',
      title: 'Calendar',
      role: 'dashboard'
    }
  },
  // ------------ Finance ------------
  {
    path: '/springbok/finance/tours',
    name: 'AdminFinanceTours',
    component: AdminFinanceTours,
    meta: { scope: 'admin', template: 'Admin', title: 'Finance - Tours', role: 'finance' },
  },
  {
    path: '/springbok/finance/tours/:id',
    name: 'AdminFinanceTour',
    component: AdminFinanceTour,
    meta: { scope: 'admin', template: 'Admin', title: 'Finance - Tour', role: 'finance' },
  },
  {
    path: '/springbok/finance/books',
    name: 'AdminFinanceBooks',
    component: AdminFinanceBooks,
    meta: { scope: 'admin', template: 'Admin', title: 'Finance - Books', role: 'finance' },
  },
  {
    path: '/springbok/finance/receivables',
    name: 'AdminFinanceReceivables',
    component: AdminFinanceReceivables,
    meta: { scope: 'admin', template: 'Admin', title: 'Finance - Receivables', role: 'finance' },
  },
  {
    path: '/springbok/finance/payables',
    name: 'AdminFinancePayables',
    component: AdminFinancePayables,
    meta: { scope: 'admin', template: 'Admin', title: 'Finance - Payables', role: 'finance' },
  },
  { path: '/springbok/finance/inflow', name: 'AdminFinanceInflow', redirect: { name: 'AdminFinanceReceivables', query: { tab: 'outstanding' } } },
  { path: '/springbok/finance/outflow', name: 'AdminFinanceOutflow', redirect: { name: 'AdminFinancePayables', query: { tab: 'bills' } } },
  { path: '/springbok/finance', redirect: { name: 'AdminFinanceTours' } },
  { path: '/springbok/finance/overview', redirect: { name: 'AdminFinanceTours' } },
  { path: '/springbok/finance/accounting', redirect: { name: 'AdminFinanceBooks', query: { tab: 'quickbooks' } } },
  { path: '/springbok/finance/payouts', redirect: { name: 'AdminFinancePayables', query: { tab: 'refunds' } } },
  {
    path: '/springbok/finance/costing',
    name: 'AdminFinanceCosting',
    redirect: { name: 'AdminProductEconomics', query: { tab: 'costing' } },
  },
  {
    path: '/springbok/product/economics',
    name: 'AdminProductEconomics',
    component: AdminProductEconomics,
    meta: {
      scope: 'admin',
      template: 'Admin',
      title: 'Product - Economics',
      role: 'product'
    }
  },
  {
    path: '/springbok/product/economics/costing/:id',
    name: 'AdminProductCostingTour',
    component: AdminProductCostingTour,
    meta: { scope: 'admin', template: 'Admin', title: 'Product - Costing', role: 'product' },
  },
  { path: '/springbok/leadership/costing', redirect: { name: 'AdminProductEconomics', query: { tab: 'costing' } } },
  {
    path: '/springbok/product/tours',
    name: 'ProductTours',
    redirect: { name: 'AdminProductDevelopment', query: { tab: 'experiences' } },
  },
  {
    path: '/springbok/product/tours/:id',
    name: 'ProductTour',
    component: ProductTour,
    meta: {
      scope: 'admin',
      template: 'Admin',
      title: 'Product - Tour',
      role: 'product'
    }
  },
  { path: '/springbok/leadership/tours',     redirect: { name: 'ProductTours' } },
  { path: '/springbok/leadership/tours/:id', redirect: to => ({ name: 'ProductTour', params: { id: to.params.id } }) },
  {
    path: '/springbok/leadership/metrics',
    name: 'AdminLeadershipMetrics',
    redirect: { name: 'AdminLeadershipInsights', query: { tab: 'digital' } },
  },
  {
    path: '/springbok/leadership/policies',
    name: 'AdminLeadershipPolicies',
    component: AdminLeadershipPolicies,
    meta: {
      scope: 'admin',
      template: 'Admin',
      title: 'Leadership - Policies',
      role: 'leadership'
    }
  },
  {
    path: '/springbok/leadership/audit-logs',
    name: 'AdminLeadershipAuditLogs',
    redirect: { name: 'AdminLeadershipPeople' },
  },
  {
    path: '/springbok/leadership/planning',
    name: 'AdminLeadershipPlanning',
    component: AdminLeadershipPlanning,
    meta: {
      scope: 'admin',
      template: 'Admin',
      title: 'Leadership - Planning',
      role: 'leadership'
    }
  },
  {
    path: '/springbok/leadership/insights',
    name: 'AdminLeadershipInsights',
    component: AdminLeadershipInsights,
    meta: {
      scope: 'admin',
      template: 'Admin',
      title: 'Leadership - Insights',
      role: 'leadership'
    }
  },
  {
    path: '/springbok/developer/api-keys-legacy',
    name: 'AdminDeveloperAPIKeys',
    component: AdminDeveloperAPIKeys,
    meta: {
      scope: 'admin',
      template: 'Admin',
      title: 'Developer - API Keys',
      role: 'technical'
    }
  },

  // ---------------------- System Routes ----------------------
  {
    path: '/lost',
    name: 'Lost',
    component: Lost,
    meta: {
      scope: 'public'
    }
  },
  {
    path: '*',
    name: 'PublicPage', 
    component: Page,
    meta: {
      scope: 'public'
    }
  }
]

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    if (to.hash) {
      return { selector: to.hash }
    }
    return { x: 0, y: 0 }
  }
})

router.beforeResolve(async (to, from, next) => {
  // Public marketing routes need no auth — resolve immediately so they are never
  // gated behind the auth bootstrap network round-trip. Login pages are public
  // but still depend on knowing the current user (to redirect if already signed
  // in), so they fall through to the gated path below.
  const isLoginRoute = ['PortalLogin', 'PartnerLogin', 'AdminLogin'].includes(to.name)
  if (to.meta.scope === 'public' && !isLoginRoute) {
    return next()
  }
  if (!isPrerenderBuild()) {
    await whenAuthBootstrapReady()
  }
  const user = getAuth().currentUser
  switch (to.meta.scope) {
    // ---------------------- Public ----------------------
    case 'public':
      // Special Case: Login Pages
      if (to.name === 'PortalLogin' || to.name === 'PartnerLogin') {
        // App.vue owns the authenticated bootstrap flow for portal/partner:
        // auth state -> sync contact claim -> hydrate session user -> redirect.
        return next();
      }
      if (to.name === 'AdminLogin') {
        if (user) {
          return next({ name: to.meta.authenticatedRoute });
        } else {
          return next();
        }
        break;
      }
      // Redirect everything else
      // if (to.name === 'Landing') {
        // Temporary redirect to booking
        // next({ name: 'Booking' });
      // } else {
        return next();
      // }
      break;

    // ---------------------- Private ----------------------
    case 'private':
      if (user) {
        next();
      } else {
        next({ name: to.meta?.loginRoute || 'PortalLogin' });
      }
      break;

    // ---------------------- Admin ----------------------
    case 'admin':
      if (!user) {
        return next({ name: 'AdminLogin' });
      }
      const routeRole = to.meta?.role;
      if (routeRole) {
        if (isBasicRole(routeRole)) {
          return next();
        }
        try {
          const permSnap = await getDoc(doc(getFirestore(), 'permissions', user.uid));
          const data = permSnap.exists() && permSnap.data() ? permSnap.data() : {};
          if (!data[routeRole]) {
            return next({ name: 'AdminDashboard' });
          }
        } catch (err) {
          console.warn('Route role check failed:', err);
          return next({ name: 'AdminDashboard' });
        }
      }
      return next();
      break;

    // ---------------------- Default ----------------------
    default:
      console.log('Default route');
      next();
  }
});

router.afterEach((to) => {
  // Skip metadata handling for Region routes (they handle their own)
  if (to.name === 'Region') {
    return
  }
  
  // Get metadata from centralized config or fallback to route meta
  const routeMeta = getMetaForRoute(to.name, to.params)
  let title = routeMeta.title || to.meta.title || 'Renedian Motorcycle Tours'
  let description = routeMeta.description || to.meta.description || 'Adventure motorcycle tours worldwide with expert guides and premium support.'
  
  // Handle dynamic titles with route parameters (for routes that still use :param syntax)
  if (title.includes(':region') && to.params.region) {
    const regionTitle = to.params.region
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
    title = title.replace(':region', regionTitle)
  }
  
  // Update page title
  document.title = title + ' | Renedian'
  
  // Update meta description
  const descriptionTag = document.querySelector('meta[name="description"]')
  if (descriptionTag) {
    descriptionTag.setAttribute('content', description)
  }
  
  // Update Open Graph meta tags
  const ogTitleTag = document.querySelector('meta[property="og:title"]')
  if (ogTitleTag) {
    ogTitleTag.setAttribute('content', title + ' | Renedian')
  }
  
  const ogDescriptionTag = document.querySelector('meta[property="og:description"]')
  if (ogDescriptionTag) {
    ogDescriptionTag.setAttribute('content', description)
  }
  
  // Update Twitter meta tags
  const twitterTitleTag = document.querySelector('meta[name="twitter:title"]')
  if (twitterTitleTag) {
    twitterTitleTag.setAttribute('content', title + ' | Renedian')
  }
  
  const twitterDescriptionTag = document.querySelector('meta[name="twitter:description"]')
  if (twitterDescriptionTag) {
    twitterDescriptionTag.setAttribute('content', description)
  }
})

export default router
